'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { createProductReview } from '@eliadmin/core/src/api/products/product-api';
import { ProductRating } from '@eliadmin/core/src/components/product/detail/product-rating';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';

import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';

import { Paragraph } from '../../ui/paragraph';
import { ModalClose, useModal } from '../../ui/modal';
import { LuX } from 'react-icons/lu';
import { Field } from '../../form/labeled-field';

export const addReviewSchema = (VM: FormValidationMessages) =>
  z.object({
    review: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    rating: z
      .number()
      .int()
      .min(1, VM.REVIEW_RATING_RANGE_VALIDATION_MESSAGE())
      .max(5, VM.REVIEW_RATING_RANGE_VALIDATION_MESSAGE()),
  });

export type AddReviewDialogProps = {
  slug: string;
};

export function AddReviewDialog(props: AddReviewDialogProps) {
  const closeDialog = useDialogStore(s => s.close);
  const modal = useModal();
  const { t } = useTranslations();

  const { me } = useCustomerStore();
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();
  const { toast } = useToast();

  const queryClient = useQueryClient();
  const createReviewMutation = useMutation({ mutationFn: createProductReview });

  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof addReviewSchema>>>({
    defaultValues: {
      rating: 1,
      review: '',
    },
    resolver: zodResolver(addReviewSchema(formValidationMessages)),
  });

  const handleSubmit = form.handleSubmit(async data => {
    if (!me?.authToken) return;

    try {
      await createReviewMutation.mutateAsync({
        authToken: me.authToken,
        body: data,
        locale,
        slug: props.slug,
      });

      toast({
        title: t('text648'),
        variant: 'success',
      });

      router.refresh();

      modal?.close();

      closeDialog(AddReviewDialog.id);

      queryClient.invalidateQueries({ queryKey: [CacheKey.PRODUCTS_REVIEWS] });
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text661'),
        description: (e as any).message,
        variant: 'destructive',
      });
    }
  });

  const GDPRhref = getDefaultUrlForTosOrGdprLocalized(ROUTES?.GDPR?.() ?? '/', locale, 'gdpr');

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('flex w-full flex-col items-start justify-start gap-6', 'ea-add-review-dialog')}
    >
      <Form {...form}>
        <div className="flex w-full items-start justify-between">
          <h4 className="font-oswald text-3xl font-medium uppercase sm:text-4xl lg:text-[40px]">
            {t('text3399')}
          </h4>
          <ModalClose asChild>
            <button type="button">
              <LuX size={24} />
            </button>
          </ModalClose>
        </div>

        <div className="flex w-full flex-col items-center justify-center gap-6 rounded-[4px] bg-[#FAF6E5] p-[18px] sm:p-5 lg:p-6">
          <h4 className="text-center font-oswald text-lg font-medium uppercase sm:text-xl lg:text-2xl">
            {t('text3400')}
          </h4>
          <FormField
            control={form.control}
            name="rating"
            render={({ field }) => (
              <FormItem required className={cn('col-span-2', 'ea-add-review-dialog__rating-field')}>
                <FormControl className={cn('', 'ea-add-review-dialog__rating-input')}>
                  <div className="w-min">
                    <ProductRating
                      rating={field.value}
                      onRatingChange={rating => form.setValue(field.name, rating)}
                      hideTextRating
                      className={{
                        main: 'flex h-10 items-center justify-start gap-2',
                        wrapper: 'h-10',
                        starIcon: 'h-10 w-10 text-[#EDB000]',
                        starDisabled: 'h-10 w-10 text-white',
                      }}
                    />
                  </div>
                </FormControl>
                <FormMessage className={cn('', 'ea-add-review-dialog__rating-message')} />
              </FormItem>
            )}
          />
        </div>

        <Field
          control={form.control}
          name="review"
          label={t('text3403')}
          placeholder={t('text3403')}
          required
          className={{ wrapper: 'w-full' }}
          type="textarea"
        />

        <Paragraph color="black" className="col-span-12">
          {t('text613')}&nbsp;
          <Link
            prefetch={false}
            href={GDPRhref}
            className="font-semibold text-primary underline transition-all hover:text-primary/80"
          >
            {t('text614')}
          </Link>
          &nbsp;{t('text3354')}
        </Paragraph>

        <Button type="submit" className="w-full sm:w-fit" isLoading={form.formState.isSubmitting}>
          {t('text2306')}
        </Button>
      </Form>
    </form>
  );
}

AddReviewDialog.id = 'ADD_REVIEW_DIALOG';
